// Sums in number boxes (Mitch, 2026-11-01): any quantity, price, cost or size box takes a sum ("40/500",
// "(2.5+0.3)*2", "$1,200/24", "=12*24") and keeps only its result, never the sum. A small calculator of its own, never
// eval(): numbers (thousands commas, a currency sign, a leading "="), + − × ÷ (also * / x), brackets and a sign in
// front. No %: in a percent box "30%" would have to mean 30, elsewhere 0.3, so it stays an error as before. Shared by
// the staff app and the vendor portal (no zod here).

type Tok = { kind: "num"; value: number } | { kind: "op"; value: string };

const CURRENCY = /[$¥€£]/g;

function tokens(text: string): Tok[] | null {
  const s = text
    .trim()
    .replace(/^=/, "")
    .replace(CURRENCY, "")
    .replace(/(\d),(?=\d)/g, "$1") // thousands commas
    .replace(/[×]/g, "*")
    .replace(/[÷]/g, "/")
    .replace(/[−–]/g, "-")
    .replace(/(?<=[\d.)\s])[xX](?=\s*[\d.(])/g, "*"); // 12x24, 12 x 24
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t") {
      i++;
      continue;
    }
    const m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i));
    if (m) {
      out.push({ kind: "num", value: Number(m[1]) });
      i += m[1].length;
      continue;
    }
    if ("+-*/()".includes(c)) {
      out.push({ kind: "op", value: c });
      i++;
      continue;
    }
    return null;
  }
  return out.length ? out : null;
}

/** Works out the tokens: sums of products of signed numbers or bracketed sums. Null when they don't read as one. */
function evaluate(toks: Tok[]): number | null {
  let at = 0;
  const peek = () => toks[at];
  const isOp = (v: string) => peek()?.kind === "op" && peek()?.value === v;
  const factor = (): number | null => {
    if (isOp("-") || isOp("+")) {
      const neg = isOp("-");
      at++;
      const v = factor();
      return v == null ? null : neg ? -v : v;
    }
    if (isOp("(")) {
      at++;
      const v = sum();
      if (v == null || !isOp(")")) return null;
      at++;
      return v;
    }
    const t = peek();
    if (t?.kind !== "num") return null;
    at++;
    return t.value;
  };
  const product = (): number | null => {
    let v = factor();
    while (v != null && (isOp("*") || isOp("/"))) {
      const op = toks[at++].value;
      const r = factor();
      if (r == null || (op === "/" && r === 0)) return null;
      v = op === "*" ? v * r : v / r;
    }
    return v;
  };
  const sum = (): number | null => {
    let v = product();
    while (v != null && (isOp("+") || isOp("-"))) {
      const op = toks[at++].value;
      const r = product();
      if (r == null) return null;
      v = op === "+" ? v + r : v - r;
    }
    return v;
  };
  const v = sum();
  return v != null && at === toks.length && Number.isFinite(v) ? v : null;
}

/**
 * The number a box's text means, a sum worked out (to `places` decimals, so 0.1+0.2 is 0.3); null when it isn't a
 * number or a sum, or is empty.
 */
export function calc(text: string, places = 6): number | null {
  const toks = tokens(text);
  const v = toks ? evaluate(toks) : null;
  if (v == null) return null;
  const f = 10 ** places;
  const r = Math.round(v * f) / f;
  return Object.is(r, -0) ? 0 : r;
}

/**
 * A box's text as Number() read it before sums (blank 0, not a number NaN), with sums worked out and thousands commas
 * and currency signs allowed: the drop-in for code that checks Number.isFinite() itself.
 */
export function numberOf(text: string | null | undefined): number {
  const t = (text ?? "").trim();
  return t === "" ? 0 : (calc(t) ?? Number.NaN);
}

/** Whether the text is a sum (not just a number): the "= result" hint shows only then. */
export function isFormula(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  const plain = Number(t.replace(CURRENCY, "").replace(/(\d),(?=\d)/g, "$1"));
  return !Number.isFinite(plain) && calc(t) != null;
}
